import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getApiUrl } from '../config/api';
import { useAuth } from '../context/AuthContext';
import { normalizeBarcodeValue } from '../utils/barcodeFormat';
import { printSaleReceipt } from '../utils/posReceipt';
import { formatSizeLabel } from '../utils/productSize';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
};

const ScanBillPanel = () => {
  const { user } = useAuth() || {};
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const loopRef = useRef(null);
  const lastScanRef = useRef({ code: '', at: 0 });
  const [manual, setManual] = useState('');
  const [cameraOn, setCameraOn] = useState(false);
  const [cameraHint, setCameraHint] = useState('');
  const [scanned, setScanned] = useState(null);
  const [sellPrice, setSellPrice] = useState('');
  const [color, setColor] = useState('');
  const [lines, setLines] = useState([]);
  const [busy, setBusy] = useState(false);
  const [looking, setLooking] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [invoice, setInvoice] = useState(null);

  const stopCamera = useCallback(() => {
    if (loopRef.current) {
      cancelAnimationFrame(loopRef.current);
      loopRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setCameraOn(false);
  }, []);

  useEffect(() => () => stopCamera(), [stopCamera]);

  const lookup = useCallback(async (raw) => {
    const code = normalizeBarcodeValue(raw);
    if (!code) return;
    const now = Date.now();
    if (lastScanRef.current.code === code && now - lastScanRef.current.at < 1500) return;
    lastScanRef.current = { code, at: now };

    setLooking(true);
    setError('');
    setNotice('');
    try {
      const res = await fetch(getApiUrl(`/api/pos/lookup/${encodeURIComponent(code)}`), {
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Product not found.');
      const found = data.barcode;
      const palette = found.colors?.length ? found.colors : found.color ? [found.color] : [];
      setScanned(found);
      setSellPrice(found.retailPrice ? String(found.retailPrice) : '');
      setColor(palette[0] || '');
      setManual('');
    } catch (err) {
      setError(err.message || 'Product not found.');
      setScanned(null);
    } finally {
      setLooking(false);
    }
  }, []);

  const startCamera = async () => {
    setError('');
    setCameraHint('');
    if (!navigator.mediaDevices?.getUserMedia) {
      setError('Camera is not available in this browser. Type the barcode or use a USB scanner.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
      });
      streamRef.current = stream;
      setCameraOn(true);
      await new Promise((resolve) => setTimeout(resolve, 50));
      const video = videoRef.current;
      if (!video) return;
      video.srcObject = stream;
      await video.play();

      if (typeof window.BarcodeDetector !== 'function') {
        setCameraHint('Live decode needs Chrome or Edge. Keep the code field focused for a USB scanner, or type the barcode.');
        return;
      }

      const detector = new window.BarcodeDetector({
        formats: ['code_128', 'code_39', 'ean_13', 'ean_8', 'upc_a'],
      });
      const tick = async () => {
        if (video.readyState >= 2) {
          try {
            const codes = await detector.detect(video);
            if (codes[0]?.rawValue) lookup(codes[0].rawValue);
          } catch (_) {
            /* keep scanning */
          }
        }
        loopRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (err) {
      setCameraOn(false);
      setError(err.message || 'Could not open camera.');
    }
  };

  const addToBill = () => {
    if (!scanned) {
      setError('Scan a barcode first.');
      return;
    }
    const price = Number(sellPrice);
    if (!Number.isFinite(price) || price < 0) {
      setError('Enter the retail price you will sell at.');
      return;
    }
    if (scanned.stock < 1) {
      setError(`${scanned.productName} (${formatSizeLabel(scanned.size)}) is out of stock.`);
      return;
    }
    setLines((current) => {
      const match = current.find(
        (item) => item.code === scanned.code && item.color === color && item.sellPrice === price
      );
      const nextQty = (match?.quantity || 0) + 1;
      if (nextQty > scanned.stock) {
        setError(`${scanned.productName} (${formatSizeLabel(scanned.size)}) is out of stock.`);
        return current;
      }
      const next = { ...scanned, quantity: nextQty, sellPrice: price, color };
      setError('');
      setNotice('Added to bill');
      if (match) return current.map((item) => (item === match ? next : item));
      return [...current, next];
    });
  };

  const removeLine = (index) => {
    setLines((current) => current.filter((_, i) => i !== index));
  };

  const total = useMemo(
    () => lines.reduce((sum, item) => sum + item.sellPrice * item.quantity, 0),
    [lines]
  );

  const generateBill = async () => {
    const working = [...lines];
    if (!working.length && scanned) {
      const price = Number(sellPrice);
      if (!Number.isFinite(price) || price < 0) {
        setError('Enter the retail price you will sell at.');
        return;
      }
      working.push({ ...scanned, quantity: 1, sellPrice: price, color });
    }
    if (!working.length) {
      setError('Scan a product first.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const res = await fetch(getApiUrl('/api/pos/sales'), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          clientSaleId: `scan-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          mode: 'custom',
          items: working.map((item) => ({
            barcodeId: item._id,
            code: item.code,
            quantity: item.quantity,
            unitPrice: item.sellPrice,
            color: item.color,
          })),
          paymentMethod: 'cash',
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Could not generate bill');
      setLines([]);
      setScanned(null);
      setSellPrice('');
      setColor('');
      setNotice('');
      setInvoice(data.sale);
      stopCamera();
    } catch (err) {
      setError(err.message || 'Could not generate bill');
    } finally {
      setBusy(false);
    }
  };

  const palette = scanned?.colors?.length
    ? scanned.colors
    : scanned?.color
      ? [scanned.color]
      : [];

  if (!user?.isAdmin) return null;

  return (
    <div className="bg-white rounded-3xl shadow-2xl p-6 sm:p-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Scan barcode</h2>
          <p className="text-sm text-gray-500 mt-1">
            Scan a product to see size, colour and wholesale price, enter the retail price you will sell at, then generate the invoice.
          </p>
        </div>
        <button
          type="button"
          onClick={cameraOn ? stopCamera : startCamera}
          className="px-4 py-2 rounded-full bg-[#56112A] text-white font-semibold shadow hover:bg-[#801C33] transition"
        >
          {cameraOn ? 'Close camera' : 'Open camera'}
        </button>
      </div>

      {cameraOn ? (
        <div className="mb-5 overflow-hidden rounded-2xl bg-black">
          <video ref={videoRef} className="w-full max-h-72 object-cover" playsInline muted autoPlay />
        </div>
      ) : null}
      {cameraHint ? <p className="text-sm text-amber-700 mb-3">{cameraHint}</p> : null}

      <form
        className="flex flex-col sm:flex-row gap-3 mb-5"
        onSubmit={(event) => {
          event.preventDefault();
          lookup(manual);
        }}
      >
        <input
          value={manual}
          onChange={(event) => setManual(event.target.value)}
          placeholder="Scan or type PROD-FARSHI-XXL-111560000"
          className="flex-1 border border-gray-200 rounded-xl px-4 py-3 uppercase tracking-wide focus:outline-none focus:ring-2 focus:ring-pink-400"
          autoCapitalize="characters"
        />
        <button
          type="submit"
          disabled={looking}
          className="px-6 py-3 rounded-xl bg-gradient-to-r from-pink-500 to-[#56112A] text-white font-semibold shadow"
        >
          {looking ? 'Looking...' : 'Go'}
        </button>
      </form>

      {error ? <p className="text-red-500 mb-4">{error}</p> : null}
      {notice ? <p className="text-green-600 mb-4">{notice}</p> : null}

      {scanned ? (
        <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-5 border border-pink-100 rounded-2xl p-4 mb-6 bg-[#FDF6F8]">
          {scanned.image ? (
            <img src={scanned.image} alt={scanned.productName} className="w-full h-44 object-cover rounded-xl" />
          ) : (
            <div className="w-full h-44 rounded-xl bg-pink-50 flex items-center justify-center text-pink-300">No image</div>
          )}
          <div>
            <h3 className="text-xl font-bold text-gray-900">{scanned.productName}</h3>
            <p className="text-xs font-semibold text-gray-500 mt-1">{scanned.code}</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
              <Fact label="Size" value={formatSizeLabel(scanned.size)} />
              <Fact label="Colour" value={color || palette.join(', ') || '—'} />
              <Fact label="Wholesale" value={`₹${scanned.wholesalePrice}`} />
              <Fact label="Stock" value={String(scanned.stock)} />
            </div>
            {palette.length > 1 ? (
              <div className="flex flex-wrap gap-2 mt-3">
                {palette.map((option) => (
                  <button
                    type="button"
                    key={option}
                    onClick={() => setColor(option)}
                    className={`px-3 h-8 rounded-full text-sm font-semibold border ${
                      color === option
                        ? 'bg-[#56112A] text-white border-[#56112A]'
                        : 'border-gray-200 text-gray-700'
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            ) : null}
            <label className="block mt-4 text-sm font-semibold text-gray-600">Retail price you will sell at</label>
            <div className="flex flex-col sm:flex-row gap-3 mt-2">
              <input
                value={sellPrice}
                onChange={(event) => setSellPrice(event.target.value)}
                type="number"
                min="0"
                placeholder="1049"
                className="flex-1 border-2 border-[#56112A] rounded-xl px-4 py-3 text-lg font-extrabold focus:outline-none focus:ring-2 focus:ring-pink-400"
              />
              <button
                type="button"
                onClick={addToBill}
                className="px-5 py-3 rounded-xl bg-white border border-pink-200 text-[#56112A] font-semibold"
              >
                Add to bill
              </button>
            </div>
          </div>
        </div>
      ) : (
        <p className="text-gray-500 mb-6">Scan a barcode to see product details, size, colour and wholesale price.</p>
      )}

      {lines.length ? (
        <div className="mb-6">
          {lines.map((item, index) => (
            <div key={`${item.code}-${index}`} className="flex items-center gap-3 py-3 border-b border-gray-100">
              <div className="flex-1">
                <p className="font-semibold text-gray-900">{item.productName}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {formatSizeLabel(item.size)}
                  {item.color ? ` · ${item.color}` : ''} · W ₹{item.wholesalePrice} · Sell ₹{item.sellPrice} × {item.quantity}
                </p>
              </div>
              <p className="font-extrabold">₹{item.sellPrice * item.quantity}</p>
              <button type="button" onClick={() => removeLine(index)} className="text-sm text-red-500">
                Remove
              </button>
            </div>
          ))}
        </div>
      ) : null}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-xl font-extrabold text-gray-900">Total ₹{total || Number(sellPrice) || 0}</p>
        <button
          type="button"
          onClick={generateBill}
          disabled={busy}
          className="px-6 py-3 rounded-full bg-gradient-to-r from-pink-500 to-[#56112A] text-white font-semibold shadow hover:from-pink-600 hover:to-[#801C33] transition disabled:opacity-60"
        >
          {busy ? 'Generating...' : 'Generate bill'}
        </button>
      </div>

      {invoice ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 relative max-h-[90vh] overflow-y-auto">
            <button
              type="button"
              className="absolute top-3 right-3 text-gray-400 hover:text-pink-500 text-2xl font-bold"
              onClick={() => setInvoice(null)}
              aria-label="Close invoice"
            >
              ×
            </button>
            <div className="text-center mb-5">
              <p className="text-sm font-semibold text-green-600">Bill generated</p>
              <h3 className="text-2xl font-extrabold text-gray-900 mt-1">Invoice</h3>
              <p className="font-extrabold text-[#56112A] mt-1">{invoice.invoiceNumber}</p>
              <p className="text-sm text-gray-500 mt-1">
                theVastra.store · {invoice.createdAt ? new Date(invoice.createdAt).toLocaleString() : 'Just now'}
              </p>
            </div>
            <div className="border border-gray-200 rounded-xl p-4">
              <div className="flex text-xs font-extrabold text-gray-500 uppercase border-b border-gray-900 pb-2 mb-2">
                <span className="flex-1">Item</span>
                <span className="w-10 text-right">Qty</span>
                <span className="w-16 text-right">Price</span>
                <span className="w-16 text-right">Amt</span>
              </div>
              {(invoice.items || []).map((item, index) => (
                <div key={index} className="flex items-start py-2">
                  <div className="flex-1 pr-3">
                    <p className="font-semibold text-gray-900">{item.productName}</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {formatSizeLabel(item.size)}
                      {item.color ? ` · ${item.color}` : ''}
                      {item.wholesalePrice != null ? ` · W ₹${item.wholesalePrice}` : ''}
                    </p>
                  </div>
                  <span className="w-10 text-right">{item.quantity}</span>
                  <span className="w-16 text-right">₹{item.unitPrice}</span>
                  <span className="w-16 text-right font-semibold">₹{item.lineTotal}</span>
                </div>
              ))}
              <div className="flex justify-between items-center pt-3 mt-2 border-t border-gray-200">
                <span className="font-bold">Total</span>
                <span className="text-2xl font-extrabold text-[#56112A]">₹{invoice.total}</span>
              </div>
              <p className="text-xs text-gray-500 mt-2 font-semibold">
                Payment: {(invoice.paymentMethod || 'cash').toUpperCase()}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 mt-5">
              <button
                type="button"
                onClick={() => printSaleReceipt(invoice)}
                className="flex-1 py-3 rounded-xl bg-[#56112A] text-white font-semibold"
              >
                Print invoice
              </button>
              <button
                type="button"
                onClick={() => setInvoice(null)}
                className="flex-1 py-3 rounded-xl border border-gray-200 font-semibold"
              >
                Scan another
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const Fact = ({ label, value }) => (
  <div>
    <p className="text-[11px] font-bold uppercase text-gray-400">{label}</p>
    <p className="font-bold text-gray-900 mt-0.5">{value}</p>
  </div>
);

export default ScanBillPanel;
